import React from 'react';
import { useSettings } from 'services/settings/SettingsContext';
import { formatDisplayDate } from 'internationalization/timezoneConversions';
import DatePicker from './DatePicker';
import '../../style/scss/datepicker.scss';

interface TimeRangePickerProps {
    fromDate: Date;
    toDate: Date;
    onChange: (fromDate: Date, toDate: Date) => void;
}

// Combines a date with a HH:mm time string into one date
function combineDateAndTime(date: Date, time: string): Date {
    const [hours, minutes] = time.split(':').map(Number);
    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);
    return combined;
}

// Formats a date into a HH:mm string for the time input
function formatTime(date: Date): string {
    return date.toTimeString().slice(0, 5);
}

// Shows the from and to calendars side by side, always open
const TimeRangePicker: React.FC<TimeRangePickerProps> = ({ fromDate, toDate, onChange }) => {
    const { userLocale } = useSettings();

    // Applies a newly picked from date, keeping its time
    const handleFromDayClick = (date: Date) => {
        onChange(combineDateAndTime(date, formatTime(fromDate)), toDate);
    };

    // Applies a newly picked to date, keeping its time
    const handleToDayClick = (date: Date) => {
        onChange(fromDate, combineDateAndTime(date, formatTime(toDate)));
    };

    return (
        <div className='time-range-picker'>
            <div className='time-range-side'>
                <div className='time-range-side-label'>{formatDisplayDate(fromDate, userLocale)}</div>
                <DatePicker
                    selectedDate={fromDate}
                    onDayClick={handleFromDayClick}
                    closeDatePicker={() => {}}
                />
                <input
                    type='time'
                    className='time-range-time-input'
                    value={formatTime(fromDate)}
                    onChange={event => onChange(combineDateAndTime(fromDate, event.target.value), toDate)}
                />
            </div>
            <div className='time-range-divider' />
            <div className='time-range-side'>
                <div className='time-range-side-label'>{formatDisplayDate(toDate, userLocale)}</div>
                <DatePicker
                    selectedDate={toDate}
                    onDayClick={handleToDayClick}
                    closeDatePicker={() => {}}
                />
                <input
                    type='time'
                    className='time-range-time-input'
                    value={formatTime(toDate)}
                    onChange={event => onChange(fromDate, combineDateAndTime(toDate, event.target.value))}
                />
            </div>
        </div>
    );
};

export default TimeRangePicker;
